import { FieldViolationsView } from "@/components/views/FieldViolationsView";
import { resolveFormValidationError } from "@/utils/formUtils";
import { useScrollToTopOnLoad } from "@/utils/hooksUtils";
import { removeStringWhiteSpaces } from "@/utils/utils";
import { App as AntApp, Card, Form } from "antd";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { t } from "i18next";
import merge from "lodash/merge";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTravelGenerate, useValidateClient, useValidateClients } from "../../../../../../common/api/queries";
import { Permission } from "../../../../../../common/security/enums";
import { FieldConstraintViolation } from "../../../../../../common/types";
import type { UUID } from "../../../../../../typings/global";
import { useCurrentAgentAccount } from "../../../../../auth/hooks";
import { CalcValidationGroup, ClientFormType, ClientType } from "../../../../../client/enums";
import { Client, CreateUpdateContractClient, NaturalClient } from "../../../../../client/types";
import { clientToCreateUpdateContractClient, processClientsDataViolations } from "../../../../../client/utils";
import { InstitutionEnum } from "../../../../../institution/enums";
import { CalcType, ClientExperience } from "../../../../enums";
import { CalcGenResultModal } from "../../../components/CalcGenResultModal";
import { CalcNavigation } from "../../../components/CalcNavigation";
import { CalcSummaryModal } from "../../../components/summary/CalcSummaryModal";
import { CalcResult, GenGainersData, GenResponse } from "../../../types";
import { filterApplicableSuccessResults, processGenResultError } from "../../../utils";
import { useTravelCalcResults } from "../../contexts";
import { TravelInsuranceType } from "../../enums";
import { TravelCalc, TravelCalcResultData, TravelFormClients, TravelGen, TravelGenForm } from "../../types";
import { TravelGenClientsDataSection } from "./sections/TravelGenClientsDataSection";
import { TravelGenOtherDataSection } from "./sections/TravelGenOtherDataSection";

const resolvePageTitle = (
  insuranceType: TravelInsuranceType,
  insuranceInstitutionName: string,
  coverage?: string,
): string => `${t("calc.travel.titles.contractGeneration")} - 
    ${t("calc.travel.titles.insuranceType." + insuranceType)}, 
    ${insuranceInstitutionName}${coverage ? `, ${t("calc.travel.sections.coverage")} ${coverage}` : ""}`;

interface Props {
  initialData?: TravelGenForm;
  calcData: TravelCalc;
  clients: TravelFormClients;
  selectedResult: CalcResult<TravelCalcResultData>;
  draftId?: UUID;
  onClientChange: (type: ClientFormType, client?: Partial<Client>) => TravelFormClients;
  handleGoToPreviousStep: VoidFunction;
}

export const TravelGenWrapper = (props: Props) => {
  const { t } = useTranslation();
  const { notification } = AntApp.useApp();

  useScrollToTopOnLoad();

  const { calcResults, calcResultsGroupedByInstitution } = useTravelCalcResults();

  const [generateResponse, setGenerateResponse] = useState<GenResponse>();
  const [summaryOpen, setSummaryOpen] = useState<boolean>(false);
  const [travelGenFormData, setTravelGenFormData] = useState<TravelGenForm>();
  const [clientsViolationErrors, setClientsViolationErrors] = useState<Map<ClientFormType, FieldConstraintViolation[]>>(
    () => new Map(),
  );

  const userAccount = useCurrentAgentAccount();

  const [form] = Form.useForm<TravelGenForm>();
  const clientValidation = useValidateClient();
  const clientsValidation = useValidateClients();
  const travelGenerate = useTravelGenerate();

  const createClientsIndicesMap = (clients: TravelFormClients): Map<ClientFormType, number> => {
    const { policyHolder, representative } = clients;
    const indices = new Map<ClientFormType, number>();
    let index = 0;

    if (policyHolder) {
      indices.set(ClientFormType.POLICY_HOLDER, index++);

      if (policyHolder.type === ClientType.LEGAL && representative) {
        indices.set(ClientFormType.REPRESENTATIVE, index);
      }
    }

    return indices;
  };

  const createContractClientsArray = (clients: TravelFormClients): CreateUpdateContractClient[] => {
    const { policyHolder, representative } = clients;
    const createUpdateClients: CreateUpdateContractClient[] = [];

    if (policyHolder) {
      createUpdateClients.push(clientToCreateUpdateContractClient(policyHolder));

      if (policyHolder.type === ClientType.LEGAL && representative) {
        createUpdateClients.push(clientToCreateUpdateContractClient(representative));
      }
    }

    return createUpdateClients;
  };

  useEffect(() => {
    if (!props.initialData) {
      const { insuredClients } = props.calcData.clientsData;

      if (insuredClients) {
        form.setFieldsValue({
          clientsData: {
            insuredClients: insuredClients.map((client) => ({
              birthDate: client.birthDate ?? "",
              firstName: "",
              lastName: "",
            })),
          },
        });
      }

      return;
    }

    let formData = structuredClone(props.initialData);
    const { financialMediationData } = formData;
    const { policyHolder, representative } = props.clients;

    if (policyHolder?.type !== ClientType.LEGAL) {
      delete formData.clientsData.representativeIdentifier;
      delete formData.clientsData.representativeFunction;
      if (representative) {
        props.onClientChange(ClientFormType.REPRESENTATIVE);
      }
    }

    if (props.calcData.clientsData.insuredClients) {
      const insuredClients = props.calcData.clientsData.insuredClients.map((client, index) => ({
        ...formData.clientsData.insuredClients?.[index],
        birthDate: client.birthDate,
      }));

      formData = {
        ...formData,
        clientsData: {
          ...formData.clientsData,
          insuredClients: insuredClients,
        },
      };
    }

    formData.financialMediationData = {
      ...financialMediationData,
      clientExperience: (policyHolder?.type === ClientType.SELF_EMPLOYED || policyHolder?.type === ClientType.LEGAL
        ? ClientExperience.SUFFICIENT
        : financialMediationData.clientExperience === ClientExperience.SUFFICIENT
          ? undefined
          : financialMediationData.clientExperience) as ClientExperience,
      recommendedResult: undefined,
    };

    form.setFieldsValue(formData);

    const clients = createContractClientsArray(props.clients);

    if (clients.length > 0) {
      clientsValidation.mutate(
        {
          prefix: "clientsData.clients",
          clients,
          validationGroup: CalcValidationGroup.GENERATE,
        },
        {
          onError: (error) => {
            if (error.violations) {
              setClientsViolationErrors(
                new Map([
                  ...clientsViolationErrors,
                  ...processClientsDataViolations(
                    createClientsIndicesMap(props.clients),
                    "clientsData.clients",
                    error.violations,
                  ),
                ]),
              );
            }
          },
        },
      );
    }
  }, [props.calcData.clientsData, props.initialData]);

  const handleGenerateFormSubmit = () => {
    form
      .validateFields()
      .then(() => {
        if (checkHasClientsError()) {
          notification.error({
            title: t("common.error"),
            description: t("common.validations.formError"),
          });
        } else {
          setSummaryOpen(true);
          setTravelGenFormData(form.getFieldsValue());
        }
      })
      .catch((errorInfo) => {
        notification.error({ title: t("common.error"), description: t("common.validations.formError") });
        resolveFormValidationError(errorInfo);
      });
  };

  const handleGenerateFormApprovalsSubmit = (gainersData?: GenGainersData) => {
    setSummaryOpen(false);
    form
      .validateFields()
      .then(() => {
        if (checkHasClientsError()) {
          notification.error({
            title: t("common.error"),
            description: t("common.validations.formError"),
          });
        } else {
          travelGenerate.mutate(processAndGetGenFormData(gainersData), {
            onSuccess: (response) => {
              setGenerateResponse(response);

              if (response?.error) {
                const violations = processGenResultError(
                  response.error,
                  createClientsIndicesMap(props.clients),
                  form,
                  "calc.travel.attrs",
                );

                if (violations) {
                  notification.error({
                    title: response.error.message,
                    description: (
                      <FieldViolationsView
                        violations={violations.notificationViolations}
                        rootPath="calc.travel.attrs"
                      />
                    ),
                    duration: 10,
                  });
                  setClientsViolationErrors(violations.clientsViolations);
                }
              }
            },
          });
        }
      })
      .catch((errorInfo) => {
        notification.error({ title: t("common.error"), description: t("common.validations.formError") });
        resolveFormValidationError(errorInfo);
      });
  };

  const handlePolicyHolderIsAlsoInsuredChange = (event: CheckboxChangeEvent) => {
    setFirstInsuredClientData(event.target.checked ? (props.clients.policyHolder as NaturalClient) : undefined);
  };

  const handleClientChange = (type: ClientFormType, client?: Partial<Client>) => {
    const clients = props.onClientChange(type, client);

    if (client) {
      clientValidation.mutate(
        {
          prefix: `clientsData.clients[${createClientsIndicesMap(clients).get(type)}]`,
          client: clientToCreateUpdateContractClient(client),
          validationGroup: CalcValidationGroup.GENERATE,
        },
        {
          onError: (error) => {
            if (error.violations) {
              setClientsViolationErrors(
                new Map([
                  ...clientsViolationErrors,
                  ...processClientsDataViolations(
                    createClientsIndicesMap(props.clients),
                    "clientsData.clients",
                    error.violations,
                  ),
                ]),
              );
            }
          },
        },
      );

      if (type === ClientFormType.POLICY_HOLDER) {
        if (client.type === ClientType.SELF_EMPLOYED || client.type === ClientType.LEGAL) {
          form.setFieldsValue({ financialMediationData: { clientExperience: ClientExperience.SUFFICIENT } });
        } else if (form.getFieldValue(["financialMediationData", "clientExperience"]) === ClientExperience.SUFFICIENT) {
          form.setFieldsValue({ financialMediationData: { clientExperience: undefined } });
        }
      }
    }

    if (type === ClientFormType.POLICY_HOLDER && form.getFieldValue(["clientsData", "policyHolderIsAlsoInsured"])) {
      if (client?.type !== ClientType.NATURAL) {
        setFirstInsuredClientData(undefined);
        form.setFieldsValue({ clientsData: { policyHolderIsAlsoInsured: false } });
        form.validateFields(["clientsData", "policyHolderIsAlsoInsured"]);
      } else {
        setFirstInsuredClientData(client as NaturalClient);
      }
    }
  };

  const handleClientViolationErrorsChange = (type: ClientFormType, violations?: FieldConstraintViolation[]) => {
    const updatedViolationsErrors = new Map<ClientFormType, FieldConstraintViolation[]>([...clientsViolationErrors]);
    if (violations?.length) {
      updatedViolationsErrors.set(type, violations);
    } else {
      updatedViolationsErrors.delete(type);
    }
    setClientsViolationErrors(updatedViolationsErrors);
  };

  const setFirstInsuredClientData = (client?: NaturalClient) => {
    const formInsuredClients = form.getFieldValue(["clientsData", "insuredClients"]);
    const insuredClients = formInsuredClients ? [...formInsuredClients] : [];

    if (client) {
      insuredClients[0] = {
        firstName: client.firstName,
        lastName: client.lastName,
        academicDegree: client.academicDegree,
        academicDegreeAfter: client.academicDegreeAfter,
        personalIdentificationNumber: client.personalIdentificationNumber,
        birthDate:
          props.calcData.generalData.insuranceType === TravelInsuranceType.CANCELLATION
            ? client.birthDate
            : insuredClients[0].birthDate,
        discountIdentifier: undefined,
      };
    } else {
      insuredClients[0] = {
        firstName: undefined,
        lastName: undefined,
        academicDegree: undefined,
        academicDegreeAfter: undefined,
        personalIdentificationNumber: undefined,
        birthDate:
          props.calcData.generalData.insuranceType === TravelInsuranceType.CANCELLATION
            ? undefined
            : insuredClients[0].birthDate,
        discountIdentifier: undefined,
      };
    }

    form.setFieldsValue({ clientsData: { insuredClients } });
  };

  const processAndGetGenFormData = (gainersData?: GenGainersData): TravelGen => {
    const values = form.getFieldsValue();
    const { calcData, clients } = props;
    const axaAssistanceYearFamilyInsurance =
      props.selectedResult.insuranceInstitution.institutionEnum === InstitutionEnum.AXA_ASSISTANCE &&
      props.calcData.generalData.insuranceType === TravelInsuranceType.YEAR &&
      props.calcData.discountsData?.familyInsurance;

    const recommendedResultData = (values.financialMediationData.recommendedResult || []) as string[];
    const institutionResults = calcResultsGroupedByInstitution
      .filter((resultsRow) => filterApplicableSuccessResults(resultsRow).length > 0)
      .map((resultsRow) => filterApplicableSuccessResults<TravelCalcResultData>(resultsRow))
      .find(
        (resultsRow) =>
          resultsRow.length > 0 &&
          resultsRow[0]?.insuranceInstitution.institutionEnum ===
            InstitutionEnum[recommendedResultData[0] as keyof typeof InstitutionEnum],
      );
    const recommendedResult =
      recommendedResultData.length > 1
        ? institutionResults?.find((result) => result.coverage === recommendedResultData[1])
        : institutionResults?.[0];

    return {
      calcResult: props.selectedResult.data as TravelCalcResultData,
      coverage: props.selectedResult.coverage,
      calcResponse: { results: calcResults },
      insuranceInstitutionId: props.selectedResult.insuranceInstitution.id,
      draftId: props.draftId,
      ...calcData,
      clientsData: {
        ...calcData.clientsData,
        clients: createContractClientsArray(clients),
        policyHolderEmail: values.clientsData.policyHolderEmail,
        policyHolderPhone: values.clientsData.policyHolderPhone,
        policyHolderMarketingConsent: values.clientsData.policyHolderMarketingConsent,
        policyHolderIsAlsoInsured: axaAssistanceYearFamilyInsurance
          ? false
          : values.clientsData.policyHolderIsAlsoInsured,
        representativeFunction: values.clientsData.representativeFunction,
        insuredClients: values.clientsData.insuredClients?.map((formClient, index) => ({
          ...(calcData.clientsData.insuredClients ? calcData.clientsData.insuredClients[index] : {}),
          ...formClient,
        })),
        insuredClientForFamilyInsurance: axaAssistanceYearFamilyInsurance
          ? clients.policyHolder?.type === ClientType.NATURAL
            ? {
                birthDate: (clients.policyHolder as NaturalClient).birthDate,
                firstName: (clients.policyHolder as NaturalClient).firstName,
                lastName: (clients.policyHolder as NaturalClient).lastName,
              }
            : values.clientsData.insuredClientForFamilyInsurance
          : undefined,
      },
      coveragesData:
        calcData.generalData.insuranceType !== TravelInsuranceType.CANCELLATION
          ? merge(
              {},
              calcData.coveragesData,
              values.coveragesData
                ? {
                    ...values.coveragesData,
                    vehicleAssistanceData: values.coveragesData.vehicleAssistanceData
                      ? {
                          ...values.coveragesData.vehicleAssistanceData,
                          licensePlate:
                            removeStringWhiteSpaces(values.coveragesData.vehicleAssistanceData.licensePlate) ?? "",
                        }
                      : undefined,
                  }
                : undefined,
            )
          : undefined,
      financialMediationData: { ...values.financialMediationData, recommendedResult },
      gainersData: gainersData ?? { gainer1Id: userAccount?.agent?.canBeGainer ? userAccount.agent.id : "" },
      type: CalcType.TRAVEL,
    };
  };

  const checkHasClientsError = () => {
    const { policyHolder, representative } = props.clients;

    const policyHolderErrors = !policyHolder || clientsViolationErrors.has(ClientFormType.POLICY_HOLDER);

    const representativeErrors =
      policyHolder?.type === ClientType.LEGAL &&
      (!representative || clientsViolationErrors.has(ClientFormType.REPRESENTATIVE));

    return policyHolderErrors || representativeErrors;
  };

  return (
    <>
      <Card
        className="card-box card-box--no-body"
        title={
          <h2>
            {resolvePageTitle(
              props.calcData.generalData.insuranceType,
              props.selectedResult.insuranceInstitution.name,
              props.selectedResult.coverage,
            )}
          </h2>
        }
      />

      <Form form={form} layout="vertical" name="travelGenForm">
        <TravelGenClientsDataSection
          calcData={props.calcData}
          clients={props.clients}
          clientsViolationErrors={clientsViolationErrors}
          selectedInstitutionEnum={props.selectedResult.insuranceInstitution.institutionEnum}
          onPolicyHolderIsAlsoInsuredChange={handlePolicyHolderIsAlsoInsuredChange}
          onClientChange={handleClientChange}
          onClientViolationErrorsChange={handleClientViolationErrorsChange}
        />

        <TravelGenOtherDataSection
          policyHolderType={props.clients.policyHolder?.type}
          calcData={props.calcData}
          selectedResult={props.selectedResult}
        />
      </Form>

      <CalcNavigation
        requiredPermission={Permission.TRAVEL_GENERATE_CONTRACT}
        onGenerateContract={handleGenerateFormSubmit}
        onGoToPrevious={props.handleGoToPreviousStep}
      />

      {travelGenFormData ? (
        <CalcSummaryModal
          open={summaryOpen}
          data={{
            calcData: props.calcData,
            selectedCalcResult: props.selectedResult,
            clients: props.clients,
            formData: travelGenFormData,
          }}
          onOkClick={(gainersData) => handleGenerateFormApprovalsSubmit(gainersData)}
          onCancelClick={() => setSummaryOpen(false)}
        />
      ) : undefined}

      <CalcGenResultModal result={generateResponse} onCancel={() => setGenerateResponse(undefined)} />
    </>
  );
};
